import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import { USER_DETAILS_URL } from "../../utils/Constant";

export default function BiddingStatusModal({ user, token, onClose, onUpdated }) {
  const paused = user.isBiddingPaused !== true;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [needsReview, setNeedsReview] = useState(false);

  async function submit(event) {
    event.preventDefault();
    if (saving || needsReview) return;
    const note = reason.trim();
    if ((paused && note.length < 3) || note.length > 500) {
      setError("Enter a reason between 3 and 500 characters.");
      return;
    }
    setSaving(true);
    setError("");
    const options = { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 };
    try {
      const response = await axios.patch(`${USER_DETAILS_URL}/${user._id}/bidding-status`, {
        paused, expectedVersion: user.biddingStatusVersion || 0, reason: note,
      }, options);
      if (!response.data?.success || response.data.data?._id !== user._id) throw new Error("The server did not confirm the change.");
      onUpdated(response.data.data, response.data.message);
      onClose();
    } catch (failure) {
      // A timed-out write may already have committed. Never automatically retry.
      setNeedsReview(true);
      const message = failure.response?.data?.error || "The change could not be confirmed.";
      let latest = "Unable to refresh the account. Refresh the Users page before trying again.";
      try {
        const response = await axios.get(`${USER_DETAILS_URL}/${user._id}`, options);
        if (!response.data?.success || response.data.data?._id !== user._id) throw new Error("Invalid account response");
        onUpdated(response.data.data);
        latest = `Current bidding status: ${response.data.data.isBiddingPaused ? "paused" : "not paused"}. Close this dialog and review the account before trying again.`;
      } catch (_) { /* Keep the safe no-retry state when refresh also fails. */ }
      setError(`${message} ${latest}`);
    } finally { setSaving(false); }
  }

  return (
    <Modal show className="bidding-users-modal" onHide={saving ? undefined : onClose} backdrop={saving ? "static" : true}
      keyboard={!saving} centered aria-labelledby="bidding-status-title">
      <form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title id="bidding-status-title">{paused ? "Pause bidding" : "Resume bidding"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1"><strong>{user.username}</strong> · Bidder #{user.userId || user._id}</p>
          <p className="text-muted small bidding-account-id">Account: {user._id}</p>
          <p>{paused ? "This stops new bids from this account, including already-open tabs. Existing bids remain valid. The bidder can still sign in and pay for existing wins."
            : "This allows new bids again. The usual activation and registration-fee requirements still apply."}</p>
          {user.biddingPauseReason && <p className="small"><strong>Last internal note:</strong> {user.biddingPauseReason}</p>}
          <label htmlFor="bidding-status-reason">{paused ? "Reason for pause" : "Resume note (optional)"}</label>
          <textarea id="bidding-status-reason" className="form-control" rows={3} maxLength={500}
            minLength={paused ? 3 : undefined} required={paused} autoFocus
            value={reason} onChange={(event) => setReason(event.target.value)} disabled={saving || needsReview}
            aria-describedby="bidding-reason-help" />
          <p id="bidding-reason-help" className="small text-muted mt-2">Internal administrator note. The action, reason and time are recorded. No message is sent to the bidder.</p>
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className={`btn ${paused ? "btn-warning" : "btn-success"}`}
            disabled={saving || needsReview || (paused && reason.trim().length < 3)}>
            {saving ? "Saving…" : paused ? "Confirm pause" : "Confirm resume"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
